import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLines } from "./Reveal";
import { ArrowDown } from "lucide-react";
import AYMark from "./AYMark";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const yMark = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pt-28 lg:px-12"
    >
      {/* Ambient glow / spotlight */}
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute -top-40 left-1/2 h-[620px] w-[620px] -translate-x-1/3 rounded-full bg-steel/25 blur-[140px]"
      />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-steel-light/10 blur-[130px]" />

      {/* Faint grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "90px 90px",
        }}
      />

      {/* Oversized AY watermark */}
      <motion.div
        style={{ y: yMark }}
        aria-hidden="true"
        className="pointer-events-none absolute -right-20 top-1/2 hidden -translate-y-1/2 text-steel-light/[0.08] lg:block"
      >
        <AYMark className="h-[560px] w-[560px]" strokeWidth={1.6} />
      </motion.div>

      <div className="relative mx-auto w-full max-w-[1400px]">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.35 }}
          className="mb-8 flex items-center gap-4 font-body text-xs font-semibold uppercase tracking-[0.3em] text-steel-light"
        >
          <span className="h-px w-10 bg-steel-light/60" />
          Al Yakhour LLC — Holding Group
        </motion.p>

        <h1 className="font-display text-[2.6rem] font-extrabold leading-[1.02] tracking-tighter text-cloud sm:text-6xl lg:text-[5.4rem]">
          <MaskLines
            animateOnLoad
            delay={0.4}
            lines={[
              "A multi-vertical group",
              "building at the edge of",
            ]}
          />
          <MaskLines
            animateOnLoad
            delay={0.75}
            lineClassName="text-steel-light"
            lines={["technology, media", "& hospitality."]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 max-w-xl font-body text-base font-light leading-relaxed text-fog sm:text-lg"
        >
          We build and back companies across three disciplines — engineering
          AI-native software, shaping brands, and investing in real-world
          hospitality. One group, many verticals.
        </motion.p>
      </div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-10 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2 text-fog"
      >
        <span className="font-body text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
}
